import { LitElement, html, css, type PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { applyRole, applyResponsiveProp, parseProp, resolveGap, resolvePad } from '../utils.js';

/**
 * uwc-stack — Flex layout (column or row)
 *
 * The primary one-dimensional layout primitive. Defaults to a vertical column;
 * switch to horizontal with `direction="row"`. Use the responsive shorthand
 * to change direction at breakpoints: `direction="column md:row"`.
 *
 * Philosophy: reach for `uwc-stack` when you need to arrange items along a
 * single axis — a page shell, a card body, a button group. Nest stacks freely;
 * they never bleed layout state into each other.
 *
 * Breakpoints: sm ≥ 640px · md ≥ 768px · lg ≥ 1024px · xl ≥ 1280px
 *
 * @attr {string}  direction - Flex direction. Default: column. Responsive: "column md:row"
 * @attr {string}  gap       - Gap token (none xs sm md lg xl 2xl) or any CSS value
 * @attr {string}  align     - align-items value. Responsive. Default: stretch
 * @attr {string}  justify   - justify-content value. Responsive. Default: flex-start
 * @attr {boolean} wrap      - Enable flex-wrap
 * @attr {string}  as        - Semantic landmark role
 * @attr {string}  pad       - Padding token or any CSS value
 *
 * @cssprop --uwc-stack-gap     - Override gap
 * @cssprop --uwc-stack-pad     - Override padding
 */
export class UwcStack extends LitElement {
  static override styles = css`
    :host {
      display:         flex;
      width:           100%;
      min-width:       0;
      flex-direction:  var(--_dir,     column);
      gap:             var(--uwc-stack-gap, var(--_gap, 0));
      align-items:     var(--_align,   stretch);
      justify-content: var(--_justify, flex-start);
      flex-wrap:       var(--_wrap,    nowrap);
      padding:         var(--uwc-stack-pad, var(--_pad, 0));
      box-sizing:      border-box;
    }

    /* ── Responsive direction/alignment ──────────────────────────────────────── */
    @media (min-width: 640px) {
      :host {
        flex-direction:  var(--_dir-sm,     var(--_dir,     column));
        align-items:     var(--_align-sm,   var(--_align,   stretch));
        justify-content: var(--_justify-sm, var(--_justify, flex-start));
      }
    }
    @media (min-width: 768px) {
      :host {
        flex-direction:  var(--_dir-md,     var(--_dir-sm,     var(--_dir,     column)));
        align-items:     var(--_align-md,   var(--_align-sm,   var(--_align,   stretch)));
        justify-content: var(--_justify-md, var(--_justify-sm, var(--_justify, flex-start)));
      }
    }
    @media (min-width: 1024px) {
      :host {
        flex-direction:  var(--_dir-lg,     var(--_dir-md,     var(--_dir-sm,     var(--_dir,     column))));
        align-items:     var(--_align-lg,   var(--_align-md,   var(--_align-sm,   var(--_align,   stretch))));
        justify-content: var(--_justify-lg, var(--_justify-md, var(--_justify-sm, var(--_justify, flex-start))));
      }
    }
    @media (min-width: 1280px) {
      :host {
        flex-direction:  var(--_dir-xl,     var(--_dir-lg,     var(--_dir-md,     var(--_dir-sm,     var(--_dir,     column)))));
        align-items:     var(--_align-xl,   var(--_align-lg,   var(--_align-md,   var(--_align-sm,   var(--_align,   stretch)))));
        justify-content: var(--_justify-xl, var(--_justify-lg, var(--_justify-md, var(--_justify-sm, var(--_justify, flex-start)))));
      }
    }
  `;

  /** Flex direction. Supports responsive syntax: "column md:row". */
  @property({ type: String })                direction = 'column';
  /** Gap between items. */
  @property({ type: String })                gap       = '';
  /** align-items. Supports responsive syntax. */
  @property({ type: String })                align     = '';
  /** justify-content. Supports responsive syntax. */
  @property({ type: String })                justify   = '';
  /** Enable flex-wrap. */
  @property({ type: Boolean })               wrap      = false;
  /** Maps to an ARIA landmark role. */
  @property({ type: String, reflect: true }) as        = '';
  /** Padding token or CSS value. */
  @property({ type: String })                pad       = '';

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('as'))        applyRole(this, this.as);
    if (changed.has('gap'))       this.style.setProperty('--_gap',  resolveGap(this.gap));
    if (changed.has('pad'))       this.style.setProperty('--_pad',  resolvePad(this.pad));
    if (changed.has('wrap'))      this.style.setProperty('--_wrap', this.wrap ? 'wrap' : 'nowrap');
    if (changed.has('direction')) applyResponsiveProp(this, parseProp(this.direction || 'column'),    '--_dir');
    if (changed.has('align'))     applyResponsiveProp(this, parseProp(this.align     || 'stretch'),   '--_align');
    if (changed.has('justify'))   applyResponsiveProp(this, parseProp(this.justify   || 'flex-start'),'--_justify');
  }

  override render() { return html`<slot></slot>`; }
}

/**
 * uwc-flex — alias for uwc-stack with direction defaulting to "row".
 *
 * Semantically identical to `<uwc-stack direction="row">`. Use whichever
 * reads more naturally in context.
 */
export class UwcFlex extends UwcStack {
  override direction = 'row';
}
